import api from '@/lib/axios';
import {
  AttendantDriveRole,
  AvailableDriveFoldersResponse,
  ClientDriveFolderLink,
  ClientDriveFoldersAccessResponse,
} from '@/features/clients/types/client-drive-folders.types';

export interface Client {
  id: string;
  tenantId: string;
  name: string;
  tradeName?: string | null;
  cnpj?: string | null;
  openingDate?: string | null;
  notionBoardId?: string | null;
  isActive: boolean;
  accountManagerId?: string | null;
  companyId?: string | null;
  createdAt: string;
  updatedAt: string;
  notionBoard?: {
    id: string;
    name: string;
    description?: string | null;
  } | null;
  accountManager?: {
    id: string;
    email: string;
  } | null;
  company?: {
    id: string;
    name: string;
  } | null;
  reimbursementMonthlyBudget?: string | number | null;
  notebookLmUrl?: string | null;
  clientType: 'internal' | 'external';
  attendantsSummary?: string | null;
  hasActiveContract?: boolean;
  contacts?: ClientContact[];
  contracts?: ClientContract[];
}

export interface ClientContact {
  id: string;
  clientId: string;
  tenantId: string;
  name: string;
  email: string;
  phone?: string | null;
  role: string;
  isPrimary: boolean;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClientContract {
  id: string;
  tenantId: string;
  clientId: string;
  contractType: string;
  parentContractId?: string | null;
  startDate: string;
  endDate: string;
  noticePeriodDays: number;
  renewalType: string;
  monthlyValue: string;
  setupValue: string;
  currency: string;
  adjustmentIndex?: string | null;
  adjustmentBaseDate?: string | null;
  adjustedAt?: string | null;
  nextAdjustmentDate?: string | null;
  lastAdjustmentDate?: string | null;
  lastAdjustmentPercentage?: string | null;
  filePath?: string | null;
  storageKey?: string | null;
  scope?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateClientDto {
  name: string;
  tradeName?: string;
  cnpj?: string;
  openingDate?: string;
  notionBoardId?: string | null;
  accountManagerId?: string;
  companyId?: string;
  isActive?: boolean;
  reimbursementMonthlyBudget?: number;
  notebookLmUrl?: string | null;
  clientType?: 'internal' | 'external';
}

export interface UpdateClientDto {
  name?: string;
  tradeName?: string;
  cnpj?: string;
  openingDate?: string;
  notionBoardId?: string | null;
  accountManagerId?: string;
  companyId?: string | null;
  isActive?: boolean;
  reimbursementMonthlyBudget?: number;
  notebookLmUrl?: string | null;
  clientType?: 'internal' | 'external';
}

export interface CreateClientContactDto {
  name: string;
  email: string;
  phone?: string;
  role: string;
  isPrimary?: boolean;
  notes?: string;
}

export interface UpdateClientContactDto {
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
  isPrimary?: boolean;
  notes?: string;
}

export interface ClientNote {
  id: string;
  clientId: string;
  tenantId: string;
  content: string;
  createdBy: string | null;
  createdAt: string;
  author: { id: string; email?: string } | null;
}

export interface CreateClientNoteDto {
  content: string;
}

export const clientsService = {
  async listClients(filters?: {
    search?: string;
    isActive?: boolean;
    companyIds?: string[];
    clientType?: 'internal' | 'external';
  }): Promise<Client[]> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
    if (filters?.companyIds?.length) {
      params.append('companyIds', filters.companyIds.join(','));
    }
    if (filters?.clientType) {
      params.append('clientType', filters.clientType);
    }
    
    const response = await api.get<Client[]>(`/clients?${params.toString()}`);
    return response.data;
  },

  async getClient(id: string): Promise<Client> {
    const response = await api.get<Client>(`/clients/${id}`);
    return response.data;
  },

  async createClient(data: CreateClientDto): Promise<Client> {
    const response = await api.post<Client>('/clients', data);
    return response.data;
  },

  async updateClient(id: string, data: UpdateClientDto): Promise<Client> {
    const response = await api.patch<Client>(`/clients/${id}`, data);
    return response.data;
  },

  async deleteClient(id: string): Promise<void> {
    await api.delete(`/clients/${id}`);
  },

  async linkNotionBoard(id: string, notionBoardId: string): Promise<void> {
    await api.patch(`/clients/${id}/link-notion-board`, { notionBoardId });
  },

  // Client Contacts
  async listClientContacts(clientId: string): Promise<ClientContact[]> {
    const response = await api.get<ClientContact[]>(`/clients/${clientId}/contacts`);
    return response.data;
  },

  async createClientContact(
    clientId: string,
    data: CreateClientContactDto,
  ): Promise<ClientContact> {
    const response = await api.post<ClientContact>(
      `/clients/${clientId}/contacts`,
      data,
    );
    return response.data;
  },

  async updateClientContact(
    clientId: string,
    contactId: string,
    data: UpdateClientContactDto,
  ): Promise<ClientContact> {
    const response = await api.patch<ClientContact>(
      `/clients/${clientId}/contacts/${contactId}`,
      data,
    );
    return response.data;
  },

  async deleteClientContact(clientId: string, contactId: string): Promise<void> {
    await api.delete(`/clients/${clientId}/contacts/${contactId}`);
  },

  // Client Notes (observações)
  async listDriveFolders(clientId: string): Promise<ClientDriveFolderLink[]> {
    const res = await api.get<ClientDriveFolderLink[]>(
      `/clients/${clientId}/drive-folders`,
    );
    return res.data;
  },

  async listAvailableDriveFolders(
    clientId: string,
    search?: string,
  ): Promise<AvailableDriveFoldersResponse> {
    const res = await api.get<AvailableDriveFoldersResponse>(
      `/clients/${clientId}/drive-folders/available`,
      { params: search ? { search } : undefined },
    );
    return res.data;
  },

  async linkDriveFolder(
    clientId: string,
    managedFolderId: string,
    tag: import('../utils/drive-folder-tags').ClientDriveFolderTag,
  ): Promise<{ id: string }> {
    const res = await api.post(`/clients/${clientId}/drive-folders`, {
      managedFolderId,
      tag,
    });
    return res.data;
  },

  async updateDriveFolderTag(
    clientId: string,
    linkId: string,
    tag: import('../utils/drive-folder-tags').ClientDriveFolderTag,
  ): Promise<{ id: string; tag: string }> {
    const res = await api.patch(`/clients/${clientId}/drive-folders/${linkId}`, {
      tag,
    });
    return res.data;
  },

  async unlinkDriveFolder(clientId: string, linkId: string): Promise<void> {
    await api.delete(`/clients/${clientId}/drive-folders/${linkId}`);
  },

  async getDriveFoldersAccess(
    clientId: string,
  ): Promise<ClientDriveFoldersAccessResponse> {
    const res = await api.get<ClientDriveFoldersAccessResponse>(
      `/clients/${clientId}/drive-folders/access`,
    );
    return res.data;
  },

  async grantDriveFolderAccess(
    clientId: string,
    linkId: string,
    userId: string,
    role: AttendantDriveRole,
  ): Promise<void> {
    await api.post(
      `/clients/${clientId}/drive-folders/${linkId}/access/${userId}`,
      { role },
    );
  },

  async revokeDriveFolderAccess(
    clientId: string,
    linkId: string,
    userId: string,
  ): Promise<void> {
    await api.delete(
      `/clients/${clientId}/drive-folders/${linkId}/access/${userId}`,
    );
  },

  async listClientNotes(clientId: string): Promise<ClientNote[]> {
    const response = await api.get<ClientNote[]>(`/clients/${clientId}/notes`);
    return response.data;
  },

  async createClientNote(
    clientId: string,
    data: CreateClientNoteDto,
  ): Promise<ClientNote> {
    const response = await api.post<ClientNote>(
      `/clients/${clientId}/notes`,
      data,
    );
    return response.data;
  },

  async deleteClientNote(clientId: string, noteId: string): Promise<void> {
    await api.delete(`/clients/${clientId}/notes/${noteId}`);
  },
};
